import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiDownload, FiHeart, FiHome, FiChevronDown, FiChevronUp, FiCalendar } from 'react-icons/fi';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    max-width: 600px;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.25rem;
    max-width: 100%;
  }
`;

const ConfirmationCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem 0.75rem;
    margin-bottom: 0.5rem;
    border-left: none;
    border-right: none;
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--success-color) 0%, var(--success-dark) 100%);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem auto;
  box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.3);
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    margin: 0 auto 1.5rem auto;
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    margin: 0 auto 1rem auto;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0 0 2rem 0;
  line-height: 1.6;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 0 0 1.5rem 0;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin: 0 0 1rem 0;
  }
`;

const EmailNotice = styled.div`
  background: rgba(104, 211, 145, 0.1);
  border: 1px solid var(--success-color);
  border-radius: 0;
  padding: 1rem;
  margin: 2rem 0;
  color: var(--success-dark);
  font-size: 0.9rem;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.85rem;
    margin: 1.5rem 0;
  }
  
  strong {
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
    margin: 1.5rem 0;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-light) 100%);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const ReceiptButton = styled(PrimaryButton)`
  background: linear-gradient(135deg, var(--success-color) 0%, var(--success-dark) 100%);
  
  &:hover {
    background: linear-gradient(135deg, var(--success-dark) 0%, var(--success-color) 100%);
  }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: var(--background-primary);
  color: var(--text-primary);
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    background: var(--background-secondary);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const DonationDetails = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin: 2rem auto;
  text-align: left;
  max-width: 500px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1.5rem auto;
    max-width: 450px;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    margin: 1rem auto;
    border-left: none;
    border-right: none;
  }
`;

const DetailsTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', sans-serif;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 0 0 1rem 0;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin: 0 0 0.75rem 0;
    padding-bottom: 0.75rem;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const DetailLabel = styled.span`
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const DetailValue = styled.span`
  font-size: 0.9rem;
  color: var(--text-primary);
  font-weight: 600;
  text-align: right;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    text-align: left;
  }
`;

const ExpandableSchedule = styled.div`
  margin-top: 1rem;
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
`;

const ScheduleToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--primary-color);
  }
  
  svg {
    transition: transform 0.2s ease;
  }
`;

const ScheduleList = styled(motion.div)`
  margin-top: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
`;

const ScheduleItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-color);
  font-size: 0.85rem;
  font-family: 'Inter', sans-serif;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const ScheduleDate = styled.span`
  color: var(--text-primary);
  font-weight: 500;
`;

const ScheduleAmount = styled.span`
  color: var(--text-primary);
  font-weight: 600;
`;

const DonationAmount = styled.div`
  background: linear-gradient(135deg, rgba(216, 184, 137, 0.1) 0%, rgba(216, 184, 137, 0.05) 100%);
  border: 2px solid var(--primary-color);
  border-radius: 0;
  padding: 2rem;
  margin: 2rem auto;
  text-align: center;
  max-width: 400px;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1.5rem auto;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin: 1rem auto;
  }
`;

const AmountLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const AmountValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

function DonationConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scheduleExpanded, setScheduleExpanded] = useState(false);
  
  // Get donation data from location state or URL params (when Stripe redirects)
  let donationData = location.state?.donationData;
  
  // If no state data, try to get from URL params (Stripe redirect)
  if (!donationData) {
    const searchParams = new URLSearchParams(location.search);
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
    
    if (paymentIntent || paymentIntentClientSecret) {
      // Redirect to donation form if we don't have full data
      // This shouldn't normally happen, but handle gracefully
      setTimeout(() => {
        navigate('/donation');
      }, 2000);
      
      return (
        <Container>
          <ConfirmationCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Subtitle>Processing your donation confirmation...</Subtitle>
          </ConfirmationCard>
        </Container>
      );
    }
  }
  
  if (!donationData) {
    return (
      <Container>
        <ConfirmationCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Subtitle>No donation information found. Please try again.</Subtitle>
          <ButtonGroup>
            <PrimaryButton onClick={() => navigate('/donation')}>
              <FiHeart size={16} />
              Make a Donation
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/dashboard')}>
              <FiHome size={16} />
              Dashboard
            </SecondaryButton>
          </ButtonGroup>
        </ConfirmationCard>
      </Container>
    );
  }

  const { 
    donationType, // 'one-time' or 'monthly'
    oneTimeAmount,
    monthlyAmount,
    numberOfPayments,
    isForever,
    totalAmount,
    user,
    receiptUrl,
    paymentIntentId,
    subscriptionScheduleId,
    subscriptionId
  } = donationData;

  // Calculate start and end dates for monthly donations
  const getDonationDates = () => {
    if (donationType !== 'monthly' || isForever || numberOfPayments === 'forever') {
      return null;
    }
    
    const startDate = new Date();
    const numPayments = parseInt(numberOfPayments) || 0;
    
    // End date is start date + (numberOfPayments - 1) months
    // For example, if starting today and 5 payments, end date is 4 months from now
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + (numPayments - 1));
    
    // Generate payment schedule (same day each month)
    const paymentDates = [];
    for (let i = 0; i < numPayments; i++) {
      const paymentDate = new Date(startDate);
      paymentDate.setMonth(paymentDate.getMonth() + i);
      paymentDates.push(paymentDate);
    }
    
    return {
      startDate,
      endDate,
      paymentDates
    };
  };

  const donationDates = getDonationDates();

  const handleDownloadReceipt = () => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    }
  };

  const getDonationTypeText = () => {
    if (donationType === 'one-time') {
      return 'One-Time Donation';
    } else {
      return 'Monthly Recurring Donation';
    }
  };

  const getThankYouMessage = () => {
    const name = user?.firstName || user?.first_name || 'there';
    if (donationType === 'one-time') {
      return `Thank you for your generous donation, ${name}! Your contribution makes a real difference.`;
    } else {
      return `Thank you for setting up monthly donations, ${name}! Your recurring support helps us continue our mission.`;
    }
  };

  return (
    <Container>
      <ConfirmationCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SuccessIcon>
          <FiCheckCircle size={40} color="white" />
        </SuccessIcon>
        
        <Title>Donation Confirmed!</Title>
        
        <Subtitle>
          {getThankYouMessage()}
        </Subtitle>

        {user?.email && (
          <EmailNotice>
            A confirmation email has been sent to <strong>{user.email}</strong> with your donation details{receiptUrl ? ' and receipt' : ''}.
          </EmailNotice>
        )}

        <DonationAmount>
          <AmountLabel>
            {donationType === 'one-time' ? 'Donation Amount' : 'Total Contribution'}
          </AmountLabel>
          <AmountValue>
            {donationType === 'monthly' && (isForever || numberOfPayments === 'forever')
              ? `$${parseFloat(monthlyAmount || 0).toFixed(2)}/month`
              : `$${parseFloat(totalAmount || 0).toFixed(2)}`
            }
          </AmountValue>
          {donationType === 'monthly' && monthlyAmount && (numberOfPayments || isForever) && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {isForever || numberOfPayments === 'forever'
                ? `$${parseFloat(monthlyAmount).toFixed(2)} per month (Unlimited)`
                : `$${parseFloat(monthlyAmount).toFixed(2)} per month × ${numberOfPayments} ${numberOfPayments === 1 ? 'payment' : 'payments'}`
              }
            </div>
          )}
        </DonationAmount>

        <ButtonGroup>
          {receiptUrl && (
            <ReceiptButton onClick={handleDownloadReceipt}>
              <FiDownload size={16} />
              Download Receipt
            </ReceiptButton>
          )}
          
          <PrimaryButton onClick={() => navigate('/dashboard')}>
            <FiHome size={16} />
            Go to Dashboard
          </PrimaryButton>
          
          <SecondaryButton onClick={() => navigate('/donation')}>
            <FiHeart size={16} />
            Make Another Donation
          </SecondaryButton>
        </ButtonGroup>

        <DonationDetails>
          <DetailsTitle>
            <FiHeart size={20} />
            Donation Details
          </DetailsTitle>
          
          <DetailRow>
            <DetailLabel>Donation Type:</DetailLabel>
            <DetailValue>{getDonationTypeText()}</DetailValue>
          </DetailRow>
          
          {donationType === 'one-time' && oneTimeAmount && (
            <DetailRow>
              <DetailLabel>Amount:</DetailLabel>
              <DetailValue>${parseFloat(oneTimeAmount).toFixed(2)}</DetailValue>
            </DetailRow>
          )}
          
          {donationType === 'monthly' && monthlyAmount && (numberOfPayments || isForever) && (
            <>
              <DetailRow>
                <DetailLabel>Monthly Amount:</DetailLabel>
                <DetailValue>${parseFloat(monthlyAmount).toFixed(2)}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Duration:</DetailLabel>
                <DetailValue>{isForever || numberOfPayments === 'forever' ? 'Unlimited' : `${numberOfPayments} months`}</DetailValue>
              </DetailRow>
              {!(isForever || numberOfPayments === 'forever') && (
                <DetailRow>
                  <DetailLabel>Total Commitment:</DetailLabel>
                  <DetailValue>${(parseFloat(monthlyAmount) * parseInt(numberOfPayments)).toFixed(2)}</DetailValue>
                </DetailRow>
              )}
            </>
          )}
          
          <DetailRow>
            <DetailLabel>Donation Start Date:</DetailLabel>
            <DetailValue>{new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</DetailValue>
          </DetailRow>
          
          {donationType === 'monthly' && donationDates && !isForever && numberOfPayments !== 'forever' && (
            <DetailRow>
              <DetailLabel>Donation End Date:</DetailLabel>
              <DetailValue>{donationDates.endDate.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</DetailValue>
            </DetailRow>
          )}
          
          {user && (
            <DetailRow>
              <DetailLabel>Donor:</DetailLabel>
              <DetailValue>{user.firstName || user.first_name} {user.lastName || user.last_name}</DetailValue>
            </DetailRow>
          )}
          
          {paymentIntentId && (
            <DetailRow>
              <DetailLabel>Transaction ID:</DetailLabel>
              <DetailValue style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                {paymentIntentId.substring(0, 20)}...
              </DetailValue>
            </DetailRow>
          )}
          
          {subscriptionScheduleId && (
            <DetailRow>
              <DetailLabel>Subscription Schedule ID:</DetailLabel>
              <DetailValue style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                {subscriptionScheduleId.substring(0, 20)}...
              </DetailValue>
            </DetailRow>
          )}
          
          {subscriptionId && (
            <DetailRow>
              <DetailLabel>Subscription ID:</DetailLabel>
              <DetailValue style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                {subscriptionId.substring(0, 20)}...
              </DetailValue>
            </DetailRow>
          )}
          
          {donationType === 'monthly' && donationDates && !isForever && numberOfPayments !== 'forever' && (
            <ExpandableSchedule>
              <ScheduleToggle onClick={() => setScheduleExpanded(!scheduleExpanded)}>
                <span>
                  <FiCalendar size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Payment Schedule
                </span>
                {scheduleExpanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </ScheduleToggle>
              
              {scheduleExpanded && (
                <ScheduleList
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {donationDates.paymentDates.map((date, index) => (
                    <ScheduleItem key={index}>
                      <ScheduleDate>
                        {date.toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </ScheduleDate>
                      <ScheduleAmount>
                        ${parseFloat(monthlyAmount || 0).toFixed(2)}
                      </ScheduleAmount>
                    </ScheduleItem>
                  ))}
                </ScheduleList>
              )}
            </ExpandableSchedule>
          )}
        </DonationDetails>
      </ConfirmationCard>
    </Container>
  );
}

export default DonationConfirmation;

